/**
 * jQuery Autocomplete plugin - v.1.0 (attach Ajax-powered autocomplete functionality to text inputs)
 * Released under MIT license <https://opensource.org/licenses/MIT>
 * Based on jQuery v.1.3+ <https://jquery.org/license/>
 * Copyright Alexandru Barbu <alexandru.barbu@smartbridge.ch>
 */

import './AjaxAutocompleter.scss';

import $ from 'jquery';
import { ts } from 'Shared/resources/assets/app/js/helpers/i18nHelpers';
import { getTopWindowDocument } from 'Shared/resources/assets/app/js/helpers/windowHelpers';

/**
 * Constructor function.
 *
 * @param {string} elementID Input ID
 * @param {string} ssURL Server side request URL
 * @param {array}  opt Options
 */
const AjaxAutocompleter = function (elementID, opt) {
    if (!elementID || !document.getElementById(elementID)) {
        throw new Error('Input field missing or invalid');
    }

    if (!opt.hasOwnProperty('itemsUrl') && !opt.hasOwnProperty('items')) {
        throw new Error(
            'Autocomplete requires either the itemsUrl option, or the items one, in order to define where the items are taken from'
        );
    }

    this.opt = $.extend(
        {
            itemsUrl: undefined,
            items: undefined,
            containerForm: null, // the container form element (to permit reliable identification of the input element within container, in case of multiple input elements with same ID)
            listID: 'list_container_div', // div containing the resulted UL list
            listClassName: 'autocomplete_menu', // div container class
            minChars: 3, // number of chars after the list items filter is triggered
            noMatchText: ts('No matching results found'), // the text displayed if no matches are found
            frequency: 750, // interval to attempt search launch (ms)
            match: 'all', // how to match typed word with list items text ('all' <=> everywhere OR 'start' <=> only at the item beginning)
            highlightClass: 'autocomplete_highlight', // CSS class for highlight of matching pattern in suggestion list items
            listWidth: 350, // suggestions list width (px)
            inputWidthStretching: false, // if to stretch the suggestions list to input's width
            listMinHeight: 100, // suggestions list min-height (px)
            listMaxHeight: 400, // suggestions list max-height (px)
            listZIndex: 5000, // suggestions list z-index
            loadingImgID: null, // ID of a loader image to be shown next to textbox while suggestions are loaded from server
            updateElement: null, // callback function when the input element is updated
            emptyItemText: undefined,
            beforeXHRCallback: null, // callback to be executed before the Ajax call is made (e.g update URL if necessary)
            runtimeUrlParams: {}, // URL params to be added dynamically at runtime, before doing
            globalVarName: elementID + '_autocomplete', // global name of autocomplete instance as variable under window namespace
            listValueProperty: 'ac_value', // the name of the property in the listElemsArray objects array that will be considered for match / display in the results list
            /*	e.g if objects in  listElemsArray are like {ac_value: '...', attr: '...'}, value will be used for match and added as 'ac_value' property of the LI,
                                                                and the rest will be appended as LI's attributes, e.g. <LI value=".." attr="..">value_highlighted</LI> */
            closeListOnEscape: true, // allows the suggestions list to be closed when ESC key is pressed (keycode = 27)
            method: 'GET', // method used for Ajax calls to retrieve suggestions
            hideNoResults: false, // do not show results list if no results (hide "noMatchText" list)
            onEnterCallback: function () {}, // function to be executed when pressing Enter on text field
            firstItemHighlighted: true, // auto-highlight and select the first item in the suggestions list
            showDirections: false, // show or not the directions ("To close the list, press ESC or click outside of it") div on the bottom of the suggestions list
            fulltextSearch: false, // do full substring match (default) or fulltext search match
            resultKey: '', // the string key in suggestions JSON response to get results from
        },
        opt || {}
    );

    // text input element to attach autocomplete to
    if (this.opt['containerForm']) {
        this.element = $('#' + elementID, $(this.opt['containerForm'])).get(0);
    } else {
        this.element = $('#' + elementID).get(0);
    }

    // array with all list entries/elements - as objects {'ac_value':'...', 'attr1':'...', ...}
    this.listElem = [];

    // create DIV container for UL suggestions list
    this.list = $(
        '<div id="' +
            this.opt['listID'] +
            '" class="' +
            this.opt['listClassName'] +
            '" style="width:' +
            this.opt['listWidth'] +
            'px;min-height:' +
            this.opt['listMinHeight'] +
            'px;max-height:' +
            this.opt['listMaxHeight'] +
            'px;"></div>'
    )
        .appendTo('body')
        .get(0);

    let listTitle = 'To close the list, click outside of it';
    if (this.opt.closeListOnEscape) {
        listTitle += ' or press Esc';
    }
    this.list.title = ts(listTitle);

    window[this.opt['globalVarName']] = this;

    // old and new textbox value
    this.oldVal = this.newVal = '';

    // status vars
    this.isActive = false;
    this.isOpen = false; // results list is hidden (true when shown)
    this.hasChanged = false;

    // list index and items
    this.activeIndex = undefined;
    this.listItems = null;
    this.listItemsNo = 0;

    // element typing handler
    this.element.onkeyup = this.initSearch.bind(this);
    // element keypress handler when list is open
    this.element.onkeypress = this.onKeyPress.bind(this);

    this.searchObserver = null; // returned by setTimeout
    $(this.element).attr('autocomplete', 'off');
    $(this.list).css('display', 'none');

    this.blurObserver = null;
    this.element.hasFocus = false;
    this.element.onblur = this.onBlur.bind(this);
    this.list.onblur = this.onBlur.bind(this);
    this.list.onfocus = this.onFocus.bind(this);
    this.element.onfocus = this.onFocus.bind(this);

    // set some browser related vars
    this.migrateJQueryBrowserSupport();

    this.browserVer = parseFloat($.browser.version.substr(0, 3));
    this.isIE = $.browser.msie;

    // if MSIE and the list can be closed using ESC key, we must prevent the typed text from being erased
    // (in IE, the typed text in a textbox can be erased with ESC, and this happens after keydown - first keyboard event)
    if (this.isIE && this.opt.closeListOnEscape) {
        this.element.onkeydown = this.onKeyDown.bind(this);
    }

    // other significant browsers
    const chromeRegexp = /Chrome/;
    this.isChrome = chromeRegexp.test(navigator.userAgent);

    const safariRegexp = /Apple/;
    this.isSafari = safariRegexp.test(navigator.vendor);

    // on scroll handler to reposition the list if visible when the page is scrolled down
    $(window).on('scroll', this.onScroll.bind(this));

    this.CONSTANTS = {
        selectedClassName: 'selected',
    };
};

/**
 * Instance prototype definition
 */
AjaxAutocompleter.prototype = {
    /**
     * Set browser info under jQuery instance, when not defined
     */
    migrateJQueryBrowserSupport: function () {
        if ($.browser) {
            return;
        }

        let matched, browser;

        // Use of jQuery.browser is frowned upon. (see http://api.jquery.com/jQuery.browser)
        // jQuery.uaMatch maintained for back-compatibility
        $.uaMatch = function (ua) {
            ua = ua.toLowerCase();

            const match =
                /(chrome)[ \/]([\w.]+)/.exec(ua) ||
                /(webkit)[ \/]([\w.]+)/.exec(ua) ||
                /(opera)(?:.*version|)[ \/]([\w.]+)/.exec(ua) ||
                /(msie) ([\w.]+)/.exec(ua) ||
                (ua.indexOf('compatible') < 0 && /(mozilla)(?:.*? rv:([\w.]+)|)/.exec(ua)) ||
                [];

            return {
                browser: match[1] || '',
                version: match[2] || '0',
            };
        };

        const ua = navigator.userAgent;
        matched = $.uaMatch(ua);
        browser = {};

        if (matched.browser) {
            browser[matched.browser] = true;
            browser.version = matched.version;
        }

        // Chrome is Webkit, but Webkit is also Safari.
        if (browser.chrome) {
            browser.webkit = true;
        } else if (browser.webkit) {
            browser.safari = true;
        }

        // MS IE 11 and Edge
        const trident = ua.indexOf('Trident/');
        if (trident > 0) {
            // IE 11
            browser.msie = true;
            const rv = ua.indexOf('rv:');
            browser.version = parseInt(ua.substr(rv + 3, ua.indexOf('.', rv)), 10).toString();
        }

        const edge = ua.indexOf('Edge/');
        if (edge > 0) {
            // Edge (IE 12+) => return version number
            browser.edge = true;
            browser.version = parseInt(ua.substr(edge + 5, ua.indexOf('.', edge)), 10).toString();
        }

        $.browser = browser;
    },

    /**
     * Init autocomplete suggestions search
     *
     * @param {Event} e
     */
    initSearch: function (e) {
        const evt = e || window.event;

        // test for arrow keys in IE and Chrome / Safari (they do not trigger keypress event!)
        if (evt && ($.browser.msie || this.isChrome || this.isSafari) && this.isOpen) {
            if ([37, 38, 39, 40].includes(evt.keyCode)) {
                // call onKeyPress instead!
                this.onKeyPress(evt);
                return;
            }
        }

        if (this.searchObserver) {
            clearTimeout(this.searchObserver);
        } else {
            // initiate search observer timer
            this.searchObserver = setTimeout(this.initSearch.bind(this), this.opt['frequency']);
            return;
        }

        this.oldVal = this.newVal;
        this.newVal = this.element.value.trim();

        if (this.element.value === '') {
            this.activeIndex = undefined;
            this.callUpdateElement(null);
        }

        if (this.newVal !== this.oldVal) {
            this.hasChanged = false;
            this.searchObserver = setTimeout(this.initSearch.bind(this), this.opt['frequency']);
        } else if (!this.hasChanged) {
            if (this.isOpen && this.opt.itemsUrl !== undefined) {
                this.hideList();
            }

            if (this.newVal.length >= this.opt['minChars']) {
                this.performSearch(this.newVal);
            }
        }
    },

    /**
     * Get runtime URL params if set
     *
     * @returns {string}
     */
    getRuntimeParams: function () {
        let runtimeParams = '';

        if (!$.isEmptyObject(this.opt['runtimeUrlParams'])) {
            const extraParams = [];
            let paramVal;

            for (let paramName in this.opt['runtimeUrlParams']) {
                paramVal = this.opt['runtimeUrlParams'][paramName];

                if (typeof paramVal == 'function') {
                    paramVal = paramVal.call();
                }

                if (paramVal.length) {
                    extraParams.push(paramName + '=' + encodeURIComponent(paramVal));
                }
            }

            if (extraParams.length > 0) {
                runtimeParams = '&' + extraParams.join('&');
            }
        }

        return runtimeParams;
    },

    /**
     * Escape regexp pattern string
     *
     * @param {string} str
     *
     * @returns {string}
     */
    escapeRegExpPattern: function (str) {
        return str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
    },

    /**
     * Execute the search
     *
     * @param {string} elemVal
     */
    performSearch: function (elemVal) {
        // show loader img if set
        if (this.opt['loadingImgID']) {
            $('#' + this.opt['loadingImgID']).show();
        }

        this.listElem = []; // reset elements

        if (this.opt.itemsUrl !== undefined) {
            if (this.opt.beforeXHRCallback) {
                this.opt.beforeXHRCallback(); // e.g can dynamically update URL, etc.
            }

            const and = '&';
            const equal = '=';

            let urlEnd = and;
            if (this.opt.itemsUrl.indexOf('?') === -1) {
                urlEnd = '?';
            }

            getTopWindowDocument().$.ajax({
                url: [
                    this.opt.itemsUrl,
                    urlEnd,
                    'value',
                    equal,
                    encodeURIComponent(elemVal),
                    and,
                    'match',
                    equal,
                    this.opt.match,
                    this.getRuntimeParams(),
                ].join(''),
                type: this.opt.method,
                dataType: 'json',
                success: (data) => {
                    if (this.opt['resultKey'] !== '' && typeof data[this.opt['resultKey']] != 'undefined') {
                        data = data[this.opt['resultKey']];
                    }

                    this.serverlessSearch(elemVal, this.addEmptyText(data));
                },
            });
        } else {
            this.serverlessSearch(elemVal, this.addEmptyText(this.opt.items));
        }
    },

    addEmptyText: function (items) {
        const hasEmptyText = items.some((item) => item[this.opt.listValueProperty] === this.opt.emptyItemText);

        if (this.opt.emptyItemText !== undefined && !hasEmptyText) {
            items.unshift({ [this.opt.listValueProperty]: this.opt.emptyItemText, id: '' });
        }

        return items;
    },

    serverlessSearch: function (elemVal, items) {
        // perform search in the list items array
        this.listElem = items;

        let ulStyle = 'style="';
        if (this.opt['listWidth']) {
            ulStyle += 'width:' + this.opt['listWidth'] + 'px;';
        }
        if (this.opt['listMinHeight']) {
            ulStyle += 'min-height:' + this.opt['listMinHeight'] + 'px;';
        }
        if (this.opt['listMaxHeight']) {
            ulStyle += 'max-height:' + this.opt['listMaxHeight'] + 'px;';
        }
        ulStyle += '"';

        let ulContentHtml = '<ul ' + ulStyle + '>';
        let itemRegex;
        if (!this.opt['fulltextSearch']) {
            itemRegex =
                this.opt['match'] === 'all'
                    ? new RegExp(this.escapeRegExpPattern(elemVal), 'i')
                    : new RegExp('^' + this.escapeRegExpPattern(elemVal), 'i');
        } else {
            // match any word in the typed phrase
            let valWords = elemVal.split(' ');
            let valRegexpString = '';
            let delim = '';

            for (let k = 0; k < valWords.length; k++) {
                valRegexpString += delim + valWords[k].trim();
                delim = '|';
            }
            itemRegex = new RegExp(this.escapeRegExpPattern(valRegexpString), 'i');
        }

        let highlightedVal;
        let i;
        let crtElem, crtElemValue;
        let matchFound = false;
        let crtMatches;
        let LIAttribs;
        let isEmptyText = false;

        for (i = 0; i < this.listElem.length; i++) {
            crtElem = this.listElem[i];
            crtElemValue = crtElem[this.opt.listValueProperty].toString(); // usually crtElem['value'];
            isEmptyText = this.opt.emptyItemText !== undefined && crtElemValue === this.opt.emptyItemText;
            crtMatches = crtElemValue.match(itemRegex);

            if (!isEmptyText && crtMatches === null) {
                continue;
            }

            // create the string of the rest of the attributes
            LIAttribs = '';

            for (let atr in crtElem) {
                if (crtElem[atr] !== '' && atr !== this.opt.listValueProperty) {
                    LIAttribs += ' data-' + atr + '="' + crtElem[atr] + '"';
                }
            }

            highlightedVal = isEmptyText
                ? crtMatches
                    ? crtMatches[0]
                    : ''
                : '<span class="' + this.opt['highlightClass'] + '">' + crtMatches[0] + '</span>';
            ulContentHtml +=
                '<li ' +
                this.opt.listValueProperty +
                '="' +
                this.htmlspecialchars(crtElemValue) +
                '"' +
                LIAttribs +
                '>' +
                crtElemValue.replace(itemRegex, highlightedVal) +
                '</li>';
            matchFound = true;
        }

        if (!matchFound) {
            // add not found message
            ulContentHtml +=
                '<li ' + this.opt.listValueProperty + '="' + elemVal + '">' + this.opt['noMatchText'] + '</li>';
        }

        ulContentHtml += '</ul>';

        this.onFinishSearch(ulContentHtml, matchFound);
    },

    /**
     * Actions operated when finishing the search
     *
     * @param {string}  ulContentHtml
     * @param {boolean} matchFound
     */
    onFinishSearch: function (ulContentHtml, matchFound) {
        this.hideLoadingImg();

        // do not show the list if no matches were found and the options requires to not show this
        if (!matchFound && this.opt['hideNoResults']) {
            return;
        }

        this.list.innerHTML = ulContentHtml;

        this.listItems = this.list.getElementsByTagName('li');
        this.listItemsNo = this.listItems.length;

        if (this.firstItemHighlighted && this.element.value !== '') {
            this.activeIndex = 0;
            this.listItems[this.activeIndex].className = this.CONSTANTS['selectedClassName'];
        } else {
            this.activeIndex = undefined;
        }

        if (this.listItemsNo >= 2 || this.listItems[0].innerHTML.indexOf(this.opt['noMatchText']) == -1) {
            this.attachListMouseEvents();
        } else {
            this.listItems[0].index = 0;
            this.listItems[0].onclick = this.hideList.bind(this);
            this.listItems[0].title = ts('Click to close');
        }

        this.showList();
        this.element.focus();
        this.element.hasFocus = true;

        this.listUL = this.list.getElementsByTagName('ul')[0];
        this.listUL.scrollTop = 0;
        if (this.isChrome || this.isSafari) {
            // attach onscroll property for Chrome - set focus on the list when scrolling it to keep it visible!
            this.listUL.onscroll = this.onFocus.bind(this);
        }
    },

    /**
     * Hide loading image when set
     */
    hideLoadingImg: function () {
        if (this.opt['loadingImgID']) {
            $('#' + this.opt['loadingImgID']).hide();
        }
    },

    /**
     * Show suggestions list after results were received
     */
    showList: function () {
        this.isOpen = true;

        // set absolute position and place the list, if not already
        if (!this.list.style.position) {
            this.list.style.position = 'absolute';

            this.placeList();

            // make it cover other elements
            this.list.style.zIndex = this.opt['listZIndex'];
        }

        // attach directions div when required
        if (this.opt.showDirections) {
            $(this.list).append('<div class="directions">*' + this.list.title + '</div>');
        }

        // override the list's width to input's width if required (set it now since the input is visible now for sure)
        if (this.opt['inputWidthStretching']) {
            this.list.style.width = parseInt(this.element.offsetWidth, 10) + 'px';
            $(this.list).find('ul:first').css('width', this.list.style.width);
        }

        $(this.list).fadeIn('fast');
    },

    placeList: function () {
        // get element position
        const elemPos = $(this.element).offset();

        // put list just under the text element
        $(this.list).css('top', elemPos['top'] + $(this.element).outerHeight() + 'px');
        $(this.list).css('left', elemPos['left'] + 'px');
    },

    /**
     * Hide suggestions list
     */
    hideList: function () {
        this.isOpen = false;
        this.hasChanged = false;

        $(this.list).hide();
    },

    /**
     * Attach mouse events on suggestions list items
     */
    attachListMouseEvents: function () {
        for (let i = 0; i < this.listItemsNo; i++) {
            this.listItems[i].index = i;

            this.listItems[i].onmouseover = this.handleListMouseEvents.bind(this);
            this.listItems[i].onmouseout = this.handleListMouseEvents.bind(this);
            this.listItems[i].onmousedown = this.handleListMouseEvents.bind(this);

            $(this.listItems[i]).find('span.' + this.opt['highlightClass']).onmousedown =
                this.handleListMouseEvents.bind(this);
        }
    },

    /**
     * Handle list items mouse events
     *
     * @param {Event} evt
     */
    handleListMouseEvents: function (evt) {
        const e = evt || window.event;

        if (!e) {
            return;
        }

        const evtType = e.type;
        const evtSrc = e.srcElement || e.target || null;

        if (!evtSrc) {
            return;
        }

        const evtSrcTagName = evtSrc.tagName.toLowerCase();
        const selectedLI = evtSrcTagName === 'li' ? evtSrc : evtSrc.parentNode;

        if (selectedLI && selectedLI.tagName.toLowerCase() !== 'li') {
            return;
        }

        switch (evtType) {
            case 'mouseover':
                if (
                    typeof this.activeIndex !== 'undefined' &&
                    this.listItems[this.activeIndex].className === this.CONSTANTS['selectedClassName']
                ) {
                    this.listItems[this.activeIndex].className = '';
                }
                selectedLI.className = this.CONSTANTS['selectedClassName'];
                if (this.element.value !== '') {
                    this.activeIndex = selectedLI.index;
                }
                break;

            case 'mouseout':
                selectedLI.className = '';
                break;

            case 'click':
            case 'mousedown':
                this.callUpdateElement(selectedLI);
                this.element.focus();
                break;

            default:
                break;
        }

        this.stopEvt(e);
    },

    callUpdateElement: function (element) {
        if (this.opt['updateElement']) {
            // run custom update element (performs custom actions before calling the default one);
            this.opt['updateElement'](element);
        } else {
            this.updateElement(element);
        }
    },

    /**
     * Update textbox after a suggestion is selected from the list
     *
     * @param {HTMLElement} selectedLI
     */
    updateElement: function (selectedLI) {
        this.hideList();
        this.hasChanged = true;
        this.oldVal = this.newVal;

        if (selectedLI !== null) {
            if (this.activeIndex) {
                this.newVal = this.listItems[this.activeIndex].getAttribute(this.opt.listValueProperty);
            } else {
                this.newVal = selectedLI.getAttribute(this.opt.listValueProperty);
            }

            this.newVal = this.stripSlashes(this.newVal);

            if (
                this.opt.emptyItemText !== undefined &&
                this.htmlspecialchars(this.newVal) === this.htmlspecialchars(this.opt.emptyItemText)
            ) {
                this.newVal = '';
            }
        } else {
            this.newVal = '';
        }

        this.element.value = this.newVal;
        this.oldVal = this.newVal;

        if (this.searchObserver) {
            clearTimeout(this.searchObserver);
        }
    },

    /**
     * Prevent textbox value be erased in IE when ESC is pressed (to close the suggestions list)
     *
     * @param {Event} evt
     *
     * @returns {bool}
     */
    onKeyDown: function (evt) {
        // this function is attached just for IE to prevent the text from being erased when ESC is pressed
        const e = evt || window.event;

        if (e.keyCode === 27) {
            // ESC pressed, call onKeyPress and return false for keydown
            this.onKeyPress(e);

            return false;
        }

        if (e.keyCode === 13 && (e.srcElement || e.target)?.id === this.element.id) {
            // if the source is the A/C textbox, call whatever function was set
            if (this.opt['onEnterCallback']) {
                return this.opt['onEnterCallback'].call();
            }

            return false;
        }

        if (e.target.value === '') {
            this.oldVal = this.element.value;
            this.element.value = null;
        }
    },

    /**
     * Keypress event handler
     *
     * @param {Event} evt
     *
     * @returns {bool}
     */
    onKeyPress: function (evt) {
        if (!this.isOpen) {
            return;
        }

        const e = evt || window.event;

        let stopEvent = true;

        if (e.keyCode === 9 || e.keyCode === 13) {
            // [TAB] or [Enter] keys pressed

            if (typeof this.activeIndex != 'undefined') {
                // element selected during browsing the suggestions list
                this.callUpdateElement(this.listItems[this.activeIndex]);

                this.stopEvt(e);
                return false; // prevents form submit
            } else {
                if (this.opt['onEnterCallback']) {
                    stopEvent = false;
                }
            }
        } else if (e.keyCode === 38) {
            // up arrow key pressed => select the previous item, or the last item (if we're at the beginning)

            if (typeof this.activeIndex != 'undefined') {
                this.listItems[this.activeIndex].className = '';
                this.activeIndex = this.activeIndex === 0 ? this.listItemsNo - 1 : this.activeIndex - 1;
            } else {
                this.activeIndex = this.listItemsNo - 1;
            }
            this.listItems[this.activeIndex].className = this.CONSTANTS['selectedClassName'];

            this.listUL = this.list.getElementsByTagName('ul')[0];
            if (this.activeIndex === this.listItemsNo - 1) {
                this.listUL.scrollTop = this.listUL.scrollHeight + this.listUL.offsetHeight;

                if ($.browser.msie) {
                    this.listItems[this.activeIndex].scrollIntoView(false);
                }
            }

            const selectionTop = this.listItems[this.activeIndex].offsetTop;

            if (selectionTop < this.listUL.scrollTop) {
                this.listUL.scrollTop = this.listUL.scrollTop - this.listItems[this.activeIndex].offsetHeight;
            }
        } else if (e.keyCode === 40) {
            // the down arrow key is pressed => select the next item, or the first item (if we're at the end)

            if (typeof this.activeIndex != 'undefined') {
                this.listItems[this.activeIndex].className = '';
                this.activeIndex = this.activeIndex === this.listItemsNo - 1 ? 0 : this.activeIndex + 1;
            } else {
                this.activeIndex = 0;
            }
            this.listItems[this.activeIndex].className = this.CONSTANTS['selectedClassName'];

            this.listUL = this.list.getElementsByTagName('ul')[0];
            if (this.activeIndex === 0) {
                this.listUL.scrollTop = 0;
            }

            const selectionBottom =
                this.listItems[this.activeIndex].offsetTop + this.listItems[this.activeIndex].offsetHeight;
            if (selectionBottom > this.listUL.scrollTop + this.listUL.offsetHeight) {
                this.listUL.scrollTop = this.listUL.scrollTop + this.listItems[this.activeIndex].offsetHeight;
            }
        } else if (e.keyCode === 37 || e.keyCode === 39) {
            // left / right arrow key pressed
            this.hasChanged = true;

            return;
        } else if (e.keyCode === 27 && this.opt.closeListOnEscape) {
            // ESC pressed => close the suggestions list if it's open (and it should be now), let the text as it is, don't complete anything
            if (this.searchObserver) {
                clearTimeout(this.searchObserver);
            }
            this.stopEvt(e);
            this.element.blur();
            this.hideList();

            return;
        }

        this.hasChanged = true;

        if (stopEvent) {
            this.stopEvt(e);
        }

        if (this.searchObserver) {
            clearTimeout(this.searchObserver);
        }

        this.searchObserver = setTimeout(this.initSearch.bind(this), this.opt['frequency']);
    },

    /**
     * Stop event propagation (cross-browser)
     *
     * @param {Event} e
     */
    stopEvt: function (e) {
        if (e.stopPropagation) {
            e.stopPropagation();
        }
        e.cancelBubble = true;
    },

    /**
     * Blur event handler
     *
     * @param {Event} evt
     */
    onBlur: function (evt) {
        if (!this.isOpen) {
            return;
        }

        const e = evt || window.event;

        if (!e) {
            return;
        }

        this.blurObserver = setTimeout(this.hideList.bind(this), 250);

        const id = (e.srcElement || e.target)?.id;

        if (id === this.element.id) {
            this.element.hasFocus = false;
        }
    },

    /**
     * Focus event handler
     *
     * @param {Event} evt
     */
    onFocus: function (evt) {
        if (this.blurObserver) {
            clearTimeout(this.blurObserver);
        }

        const e = evt || window.event;

        if (!e) {
            return;
        }

        const id = (e.srcElement || e.target)?.id;

        if (id === this.element.id) {
            this.element.hasFocus = true;

            if (this.opt.minChars === 0) {
                this.initSearch(evt);
                this.performSearch(this.element.value);
                this.showList();
            }
        } else {
            this.element.hasFocus = false;
        }

        if (this.isChrome && e.type === 'scroll') {
            this.element.focus();
        }
    },

    /**
     * Strip slashes (un-escape)
     *
     * @param {string} str
     *
     * @returns {string}
     */
    stripSlashes: function (str) {
        return (str + '').replace(/\0/g, '0').replace(/\\([\\'"])/g, '$1');
    },

    /**
     * Replace html entities in the provided string
     *
     * @param {string} string
     * @param {string} quoteStyle
     *
     * @returns {string}
     */
    htmlspecialchars: function (string, quoteStyle) {
        // example: htmlspecialchars("<a href='test'>Test</a>", 'ENT_QUOTES');
        // returns: '&lt;a href=&#039;test&#039;&gt;Test&lt;/a&gt;'

        let histogram;
        let entity;
        let tmpStr = string.toString();

        if (false === (histogram = this.getHtmlTranslationTable('HTML_SPECIALCHARS', quoteStyle))) {
            return false;
        }

        for (let symbol in histogram) {
            entity = histogram[symbol];
            tmpStr = tmpStr.split(symbol).join(entity);
        }

        return tmpStr;
    },

    /**
     * Get HTML translations table (char code => HTML entity)
     *
     * @param {string} table
     * @param {string} quoteStyle
     *
     * @returns {object}
     */
    getHtmlTranslationTable: function (table, quoteStyle) {
        // example: this.getHtmlTranslationTable('HTML_SPECIALCHARS');
        // returns: {'"': '&quot;', '&': '&amp;', '<': '&lt;', '>': '&gt;'}

        let entities = {},
            histogram = {},
            decimal = 0,
            symbol = '';
        let constMappingTable = {},
            constMappingQuoteStyle = {};
        let useTable, useQuoteStyle;

        // Translate arguments
        constMappingTable[0] = 'HTML_SPECIALCHARS';
        constMappingTable[1] = 'HTML_ENTITIES';
        constMappingQuoteStyle[0] = 'ENT_NOQUOTES';
        constMappingQuoteStyle[2] = 'ENT_COMPAT';
        constMappingQuoteStyle[3] = 'ENT_QUOTES';

        useTable = !isNaN(table) ? constMappingTable[table] : table ? table.toUpperCase() : 'HTML_SPECIALCHARS';
        useQuoteStyle = !isNaN(quoteStyle)
            ? constMappingQuoteStyle[quoteStyle]
            : quoteStyle
            ? quoteStyle.toUpperCase()
            : 'ENT_COMPAT';

        if (useTable !== 'HTML_SPECIALCHARS' && useTable !== 'HTML_ENTITIES') {
            return entities;
        }

        // ascii decimals for better compatibility
        entities['38'] = '&amp;';
        if (useQuoteStyle !== 'ENT_NOQUOTES') {
            entities['34'] = '&quot;';
        }
        if (useQuoteStyle === 'ENT_QUOTES') {
            entities['39'] = '&#039;';
        }
        entities['60'] = '&lt;';
        entities['62'] = '&gt;';

        // ascii decimals to real symbols
        for (decimal in entities) {
            symbol = String.fromCharCode(decimal);
            histogram[symbol] = entities[decimal];
        }

        return histogram;
    },

    /**
     * On scroll handler
     */
    onScroll: function () {
        try {
            this.placeList();
        } catch (e) {}
    },

    /**
     * Get input element's ID
     *
     * @return {HTMLElement}
     */
    getInputElement: function () {
        return this.element;
    },
};

export { AjaxAutocompleter };
